import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization, streamPersistence, createSSEConnection} from '../../../../lib/api';
import {safeConsoleError} from '../../../../lib/sanitize';
import auth from '../../../../lib/auth';
import {parseApiError} from '../../../../lib/error-handler';
import {isAbortError} from '../../../../lib/error-status';
import {SSEConnectionStore} from './sse-connection-store';

export type ProviderRostersStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

/**
 * Manages roster operations including copy, rename, delete, and roster-specific SSE coordination.
 * Handles roster revision management and reconnecting to long-running roster operations.
 *
 * Note: Roster comparison functionality has been moved to RosterComparisonStore
 */
export class RosterStore {
    private _providerRosters: any[] = [];
    loadingRevisionRecalculation = false;
    isRosterActionLoading = false;
    rosterRunCount = 0;
    loadingProviderRosters = false;
    providerRostersLoaded = false;
    providerRostersLoadFailed = false;
    // Controller of the provider roster request in flight; a request it no longer matches has been replaced
    private providerRostersRequest: AbortController | null = null;

    constructor(
        private sseConnectionStore: SSEConnectionStore,
        private showToastNotification: (kind: string, title: string, subtitle: string) => void,
        private hideToastNotification: () => void
    ) {
        makeAutoObservable(this);
    }

    /**
     * Reconnect to an existing recalculation stream after page refresh
     */
    async reconnectToRecalculationStream(streamId: string): Promise<void> {
        try {
            const streamStatus = await this.checkStreamStatus(streamId);

            if (streamStatus.status === 'completed') {
                this.handleCompletedStream(streamId);
                return;
            } else if (streamStatus.status === 'failed') {
                this.handleFailedStream(streamId, streamStatus.error);
                return;
            }

            await this.setupRecalculationSSEConnection(streamId);
        } catch (error) {
            this.handleReconnectionError(streamId, error);
        }
    }

    private async checkStreamStatus(streamId: string): Promise<{status: string; error?: string}> {
        return await networkOptimization.getStreamStatus(streamId);
    }

    private handleCompletedStream(streamId: string): void {
        runInAction(() => {
            this.rosterRunCount += 1;
            this.showToastNotification('success', 'Recalculation Complete', 'Network adequacy has been successfully updated.');
        });
        this.cleanupStream(streamId);
    }

    private handleFailedStream(streamId: string, error?: string): void {
        runInAction(() => {
            this.showToastNotification('error', 'Recalculation Failed', error || 'Please try again or contact support.');
        });
        this.cleanupStream(streamId);
    }

    private async setupRecalculationSSEConnection(streamId: string): Promise<void> {
        this.loadingRevisionRecalculation = true;
        this.showToastNotification('info', 'Reconnecting to recalculation', 'Resuming network adequacy calculation...');

        await createSSEConnection(
            streamId,
            (message: string) => this.handleSSEProgress(message),
            (status: 'COMPLETED' | 'FAILED') => {
                if (status === 'COMPLETED') {
                    this.handleSSECompletion(streamId);
                } else {
                    this.handleSSEFailure(streamId);
                }
            },
            (error) => this.handleSSEError(streamId, error)
        );
    }

    private handleSSEProgress(message: string): void {
        runInAction(() => {
            this.showToastNotification('info', 'Recalculation Progress', message);
        });
    }

    private handleSSECompletion(streamId: string): void {
        runInAction(() => {
            this.rosterRunCount += 1;
            this.hideToastNotification();
            this.showToastNotification('success', 'Recalculation Complete', 'Network adequacy has been successfully updated.');
            this.loadingRevisionRecalculation = false;
        });
        this.cleanupStream(streamId);
    }

    private handleSSEFailure(streamId: string): void {
        runInAction(() => {
            this.hideToastNotification();
            this.showToastNotification('error', 'Recalculation Failed', 'Please try again or contact support.');
            this.loadingRevisionRecalculation = false;
        });
        this.cleanupStream(streamId);
    }

    private handleSSEError(streamId: string, error: any): void {
        safeConsoleError('SSE connection error:', error);
        runInAction(() => {
            this.loadingRevisionRecalculation = false;
            this.hideToastNotification();
            this.showToastNotification('error', 'Connection Error', 'Lost connection to recalculation. Please try again.');
        });
        this.cleanupStream(streamId);
    }

    private handleReconnectionError(streamId: string, error: any): void {
        safeConsoleError('Error checking stream status:', error);
        runInAction(() => {
            this.loadingRevisionRecalculation = false;
            this.showToastNotification('error', 'Reconnection Failed', 'Could not reconnect to recalculation. Please try again.');
        });
        this.cleanupStream(streamId);
    }

    private cleanupStream(streamId: string): void {
        streamPersistence.removeStream(streamId);
        this.sseConnectionStore.removeActiveStreamId(streamId);
    }

    /**
     * Reconnect to an existing roster copy stream after page refresh
     * Handles post-copy operations if provider changes exist
     */
    async reconnectToRosterCopyStream(
        streamId: string,
        metadata: any,
        providerRosters: any[],
        addNewRevisionToRoster: (rosterId: number, revisionName: string, notes: string) => number
    ): Promise<void> {
        try {
            // First check if the stream still exists and its status
            const streamStatus = await networkOptimization.getStreamStatus(streamId);

            // Check if we need to call updateRoster after copy completes (based on provider changes)
            const hasProviderChanges = (metadata.selectedAdditionalProviderKeys?.length > 0) ||
                                        (metadata.deselectedExistingProviderKeys?.length > 0);

            if (streamStatus.status === 'completed') {
                // Job already completed while page was closed
                runInAction(() => {
                    if (metadata.rosterId && metadata.revisionName) {
                        // Prevent duplicate: only add if not already present
                        const existingRoster = providerRosters.find(r => r.rosterId === metadata.rosterId.toString() && r.revisionName === metadata.revisionName);
                        let newRevisionId = null;
                        if (!existingRoster) {
                            newRevisionId = addNewRevisionToRoster(metadata.rosterId, metadata.revisionName, metadata.notes || '');
                        } else {
                            newRevisionId = existingRoster.revisionId;
                        }
                        // If there were provider changes, trigger updateRoster
                        if (hasProviderChanges && newRevisionId) {
                            // Clear roster action loading since copy is complete
                            this.isRosterActionLoading = false;
                            this.handlePostCopyUpdate(newRevisionId, metadata);
                        } else {
                            this.showToastNotification('success', 'Roster copied successfully', `${metadata.revisionName || 'Roster'} has been created`);
                            this.isRosterActionLoading = false;
                        }
                    }
                });
                streamPersistence.removeStream(streamId);
                this.sseConnectionStore.removeActiveStreamId(streamId);
                return;
            } else if (streamStatus.status === 'failed') {
                // Job failed while page was closed
                runInAction(() => {
                    this.showToastNotification('error', 'Roster Copy Failed', streamStatus.error || 'Please try again or contact support.');
                    this.isRosterActionLoading = false;
                });
                streamPersistence.removeStream(streamId);
                this.sseConnectionStore.removeActiveStreamId(streamId);
                return;
            }

            // Stream still running - connect to it
            this.isRosterActionLoading = true;
            this.showToastNotification('info', 'Reconnecting to roster copy', 'Resuming roster copy operation...');

            await createSSEConnection(
                streamId,
                (message: string) => {
                    // Show progress as toast notification
                    runInAction(() => {
                        this.showToastNotification('info', 'Roster Copy Progress', message);
                    });
                },
                (status: 'COMPLETED' | 'FAILED') => {
                    if (status === 'COMPLETED') {
                        runInAction(() => {
                            if (metadata.rosterId && metadata.revisionName) {
                                // Prevent duplicate: only add if not already present
                                const existingRoster = providerRosters.find(r => r.rosterId === metadata.rosterId.toString() && r.revisionName === metadata.revisionName);
                                let newRevisionId = null;
                                if (!existingRoster) {
                                    newRevisionId = addNewRevisionToRoster(metadata.rosterId, metadata.revisionName, metadata.notes || '');
                                } else {
                                    newRevisionId = existingRoster.revisionId;
                                }
                                // If there were provider changes, trigger updateRoster
                                if (hasProviderChanges && newRevisionId) {
                                    this.hideToastNotification();
                                    // Clear roster action loading since copy is complete
                                    this.isRosterActionLoading = false;
                                    this.handlePostCopyUpdate(newRevisionId, metadata);
                                } else {
                                    this.hideToastNotification();
                                    this.showToastNotification('success', 'Roster copied successfully', `${metadata.revisionName || 'Roster'} has been created`);
                                    this.isRosterActionLoading = false;
                                }
                            }
                        });
                    } else {
                        runInAction(() => {
                            this.hideToastNotification();
                            this.showToastNotification('error', 'Roster Copy Failed', 'Please try again or contact support.');
                            this.isRosterActionLoading = false;
                        });
                    }
                    // Clean up
                    streamPersistence.removeStream(streamId);
                    this.sseConnectionStore.removeActiveStreamId(streamId);
                },
                (error) => {
                    safeConsoleError('SSE connection error:', error);
                    runInAction(() => {
                        this.isRosterActionLoading = false;
                        this.hideToastNotification();
                        this.showToastNotification('error', 'Connection Error', 'Lost connection to roster copy. Please try again.');
                    });
                    // Clean up on error
                    streamPersistence.removeStream(streamId);
                    this.sseConnectionStore.removeActiveStreamId(streamId);
                }
            );
        } catch (error) {
            // Stream not found or error checking status
            safeConsoleError('Error checking stream status:', error);
            runInAction(() => {
                this.isRosterActionLoading = false;
                this.showToastNotification('error', 'Reconnection Failed', 'Could not reconnect to roster copy. Please try again.');
            });
            // Clean up the stale stream reference
            streamPersistence.removeStream(streamId);
            this.sseConnectionStore.removeActiveStreamId(streamId);
        }
    }

    /**
     * Handle post-copy update operations after roster copy completes
     */
    private async handlePostCopyUpdate(newRevisionId: number, metadata: any): Promise<void> {
        try {
            const controller = new AbortController();
            await this.updateRosterFromMetadata(
                metadata.runId,
                newRevisionId,
                metadata.specialtyFilterCode,
                metadata.selectedAdditionalProviderKeys,
                metadata.deselectedExistingProviderKeys,
                metadata.county,
                metadata.standardType,
                metadata.standardVersion,
                metadata.patientRosterId,
                controller
            );
        } catch (error) {
            safeConsoleError('Error during post-copy update:', error);
            runInAction(() => {
                this.showToastNotification('error', 'Failed to apply changes', 'Roster was copied but changes could not be applied.');
                this.isRosterActionLoading = false;
            });
        }
    }

    /**
     * Update roster with explicit parameters instead of reading from store
     * Used for post-copy operations to avoid store state conflicts
     */
    async updateRosterFromMetadata(
        runId: number,
        revisionId: number,
        specialtyFilterCode: string,
        selectedAdditionalProviderKeys: string[],
        deselectedExistingProviderKeys: string[],
        county: string,
        standardType: string,
        standardVersion: string,
        patientRosterId: string,
        controller: AbortController
    ): Promise<void> {
        if (!runId || !specialtyFilterCode || (selectedAdditionalProviderKeys.length === 0 && deselectedExistingProviderKeys.length === 0))
            return;

        if (this.loadingRevisionRecalculation) {
            this.showToastNotification('warning', 'Recalculation in Progress', 'Please wait for the current recalculation to complete.');
            return;
        }

        try {
            this.loadingRevisionRecalculation = true;
            this.showToastNotification('info', 'Recalculation Started', 'Analyzing network adequacy...');

            await networkOptimization.updateProviderRoster(runId, revisionId, specialtyFilterCode, selectedAdditionalProviderKeys, deselectedExistingProviderKeys, controller.signal);

            // Start SSE-based recalculation
            const {stream_id} = await networkOptimization.recalculateRevisionSSE(
                runId,
                revisionId,
                specialtyFilterCode,
                county,
                standardType,
                standardVersion,
                patientRosterId,
                controller.signal
            );

            // Save stream to sessionStorage for reconnection after page refresh
            streamPersistence.saveStream(stream_id, {
                streamType: 'recalculation',
                runId: runId,
                revisionId: revisionId,
                specialtyCode: specialtyFilterCode,
                timestamp: Date.now()
            });

            // Connect to SSE stream for progress updates
            await new Promise<void>(async (resolve, reject) => {
                const es = await createSSEConnection(
                    stream_id,
                    (message: string) => {
                        runInAction(() => {
                            this.showToastNotification('info', 'Recalculation Progress', message);
                        });
                    },
                    (status: 'COMPLETED' | 'FAILED') => {
                        if (status === 'COMPLETED') {
                            runInAction(() => {
                                this.rosterRunCount += 1;
                                this.hideToastNotification();
                                this.showToastNotification('success', 'Recalculation Complete', 'Network adequacy has been successfully updated.');
                            });
                            streamPersistence.removeStream(stream_id);
                            resolve();
                        } else {
                            runInAction(() => {
                                this.hideToastNotification();
                                this.showToastNotification('error', 'Recalculation Failed', 'Please try again or contact support.');
                            });
                            streamPersistence.removeStream(stream_id);
                            reject(new Error('Recalculation failed'));
                        }
                    },
                    (error) => {
                        safeConsoleError('SSE connection error:', error);
                    }
                );

                controller.signal.addEventListener('abort', () => {
                    es.close();
                    streamPersistence.removeStream(stream_id);
                    reject(new Error('Aborted'));
                });
            });
        } catch (error) {
            this.hideToastNotification();
            runInAction(() => {
                this.showToastNotification('error', 'Roster Update Failed', 'Please try again or contact support.');
            });
            throw error;
        }
        finally{
            this.loadingRevisionRecalculation = false;
        }
    }

    get providerRosters(): any[] {
        return this._providerRosters;
    }

    set providerRosters(value: any[]) {
        this._providerRosters = value;
        this.providerRostersLoaded = true;
        this.providerRostersLoadFailed = false;
    }

    /**
     * Where the provider roster list is in its lifecycle, so views can render a state per outcome.
     * 'idle' means nothing has loaded and no request is in flight (e.g. the last one was aborted).
     * Once a list has loaded it stays 'ready' or 'empty'; a later failed refresh keeps the data.
     */
    get providerRostersStatus(): ProviderRostersStatus {
        if (this.providerRostersLoaded) {
            return this._providerRosters.length > 0 ? 'ready' : 'empty';
        }
        if (this.loadingProviderRosters) {
            return 'loading';
        }
        return this.providerRostersLoadFailed ? 'error' : 'idle';
    }

    async loadProviderRosters(controller: AbortController): Promise<void> {
        // Share the request in flight rather than repeat it - unless its caller has already aborted it
        // (e.g. StrictMode's unmount), as it would then end with no list
        if (this.loadingProviderRosters && !this.providerRostersRequest?.signal.aborted) {
            return;
        }

        try {
            runInAction(() => {
                this.providerRostersRequest = controller;
                this.loadingProviderRosters = true;
                this.providerRostersLoadFailed = false;
            });
            const data = await networkOptimization.listProviderRosters(controller.signal);
            // A replaced request no longer owns the status
            if (this.providerRostersRequest !== controller) {
                return;
            }
            runInAction(() => {
                this._providerRosters = data;
                this.providerRostersLoaded = true;
                this.loadingProviderRosters = false;
                this.providerRostersRequest = null;
            });
        } catch (error) {
            if (this.providerRostersRequest !== controller) {
                return;
            }
            // Silently handle abort errors - component unmounted or navigation occurred
            const aborted = error instanceof Error && isAbortError(error);
            runInAction(() => {
                this.loadingProviderRosters = false;
                this.providerRostersLoadFailed = !aborted;
                this.providerRostersRequest = null;
            });
            if (aborted) {
                return;
            }
            throw error;
        }
    }

    addNewRevisionToRoster(rosterId: number, revisionName: string, notes: string | null): number {
        let newRevisionIdNum = 0;
        runInAction(() => {
            // Find all rosters with the same rosterId to determine the next revision ID
            const rostersWithSameId = this._providerRosters.filter(r => r.rosterId === rosterId.toString());
            if (rostersWithSameId.length > 0) {
                // Get the base roster info from any existing roster
                const baseRoster = rostersWithSameId[0];

                // Calculate the next revision ID by finding the max revision ID and incrementing
                const maxRevisionId = Math.max(
                    ...rostersWithSameId.map((r: any) => parseInt(r.revisionId, 10))
                );
                const nextRevisionId = (maxRevisionId + 1).toString();
                newRevisionIdNum = maxRevisionId + 1;

                // Get logged-in user email from auth
                const currentUser = auth.principal?.email || '';
                const currentDate = new Date().toISOString();

                // Create a new revision
                const newRevision = {
                    createdAt: baseRoster.createdAt,
                    createdBy: baseRoster.createdBy,
                    dateModified: baseRoster.dateModified,
                    modifiedBy: baseRoster.modifiedBy,
                    revisionCreatedBy: currentUser,
                    revisionDateCreated: currentDate,
                    revisionDateModified: currentDate,
                    revisionId: nextRevisionId,
                    revisionModifiedBy: currentUser,
                    revisionName: revisionName,
                    revisionNotes: notes,
                    rosterId: rosterId.toString(),
                    rosterName: baseRoster.rosterName
                };

                this._providerRosters = [...this._providerRosters, newRevision];
            }
        });
        return newRevisionIdNum;
    }

    private updateRevisionInRoster(rosterId: number, revisionId: number, revisionName: string, notes: string | null): void {
        runInAction(() => {
            const rosterIndex = this._providerRosters.findIndex(
                r => r.rosterId === rosterId.toString() && r.revisionId === revisionId.toString()
            );

            if (rosterIndex !== -1) {
                // Update in place
                this._providerRosters[rosterIndex] = {
                    ...this._providerRosters[rosterIndex],
                    revisionName: revisionName,
                    revisionNotes: notes,
                };
                this._providerRosters = [...this._providerRosters];
            }
        });
    }

    private deleteRevisionFromRoster(rosterId: number, revisionId: number): void {
        runInAction(() => {
            // Remove the roster with the matching rosterId and revisionId
            this._providerRosters = [...this._providerRosters.filter(
                r => !(r.rosterId === rosterId.toString() && r.revisionId === revisionId.toString())
            )];
        });
    }

    resetProviderRosters(): void {
        this._providerRosters = [];
        this.loadingProviderRosters = false;
        this.providerRostersLoaded = false;
        this.providerRostersLoadFailed = false;
        this.providerRostersRequest = null;
    }

    async revertProviderRosterToOriginal(
        runId: number,
        revisionId: number,
        specialtyFilterCode: string,
        controller: AbortController
    ): Promise<void> {
        if (!runId || !specialtyFilterCode) return;
        await networkOptimization.resyncProviderRosterRevision(runId, revisionId, specialtyFilterCode, controller.signal);
    }

    async copyRoster(
        rosterId: number,
        revisionName: string,
        notes: string,
        controller: AbortController,
        selectedAdditionalProviderKeys: string[],
        deselectedExistingProviderKeys: string[],
        runId: number,
        specialtyFilterCode: string,
        county: string,
        standardType: string,
        standardVersion: string,
        patientRosterId: string,
        handleApiError: (error: unknown, title: string, retryCallback?: () => void) => void
    ): Promise<{success: boolean; message: string; revisionId: number | null}> {
        // Prevent multiple simultaneous roster copies
        if (this.isRosterActionLoading) {
            this.showToastNotification('warning', 'Roster Copy in Progress', 'Please wait for the current roster copy to complete.');
            return {success: false, message: 'Roster copy already in progress', revisionId: null};
        }

        try {
            this.isRosterActionLoading = true;
            this.showToastNotification('info', 'Roster Copy Started', 'Copying roster...');

            // Start SSE-based roster copy
            const {stream_id} = await networkOptimization.copyRosterSSE(rosterId, revisionName, notes, controller.signal);

            // Save stream to sessionStorage for reconnection after page refresh
            streamPersistence.saveStream(stream_id, {
                streamType: 'roster_copy',
                rosterId,
                revisionName,
                notes,
                selectedAdditionalProviderKeys,
                deselectedExistingProviderKeys,
                runId,
                specialtyFilterCode,
                county,
                standardType,
                standardVersion,
                patientRosterId,
                timestamp: Date.now()
            });

            let newRevisionId: number | null = null;

            // Connect to SSE stream for progress updates
            await new Promise<void>(async (resolve, reject) => {
                const es = await createSSEConnection(
                    stream_id,
                    (message: string) => {
                        runInAction(() => {
                            this.showToastNotification('info', 'Roster Copy Progress', message);
                        });
                    },
                    (status: 'COMPLETED' | 'FAILED') => {
                        if (status === 'COMPLETED') {
                            runInAction(() => {
                                newRevisionId = this.addNewRevisionToRoster(rosterId, revisionName, notes);
                                this.hideToastNotification();
                                this.showToastNotification('success', 'Roster copied successfully', `${revisionName} has been created`);
                            });
                            streamPersistence.removeStream(stream_id);
                            resolve();
                        } else {
                            runInAction(() => {
                                this.hideToastNotification();
                                this.showToastNotification('error', 'Failed to copy roster', 'Please try again');
                            });
                            streamPersistence.removeStream(stream_id);
                            reject(new Error('Roster copy failed'));
                        }
                    },
                    (error) => {
                        safeConsoleError('SSE connection error:', error);
                    }
                );

                controller.signal.addEventListener('abort', () => {
                    es.close();
                    streamPersistence.removeStream(stream_id);
                    reject(new Error('Aborted'));
                });
            });

            return {success: true, message: 'Roster copied successfully', revisionId: newRevisionId};
        } catch (error) {
            this.hideToastNotification();

            const parsedError = parseApiError(error);
            if (parsedError.isAbortError) {
                return {success: false, message: 'Aborted', revisionId: null};
            }

            handleApiError(
                error,
                'Failed to Copy Roster',
                () => {
                    const newController = new AbortController();
                    this.copyRoster(
                        rosterId, revisionName, notes, newController,
                        selectedAdditionalProviderKeys, deselectedExistingProviderKeys,
                        runId, specialtyFilterCode, county, standardType, standardVersion, patientRosterId,
                        handleApiError
                    );
                }
            );

            return {success: false, message: parsedError.message, revisionId: null};
        } finally {
            this.isRosterActionLoading = false;
        }
    }

    async renameRoster(
        rosterId: number,
        revisionId: number,
        revisionName: string,
        notes: string | null,
        controller: AbortController,
        handleApiError: (error: unknown, title: string, retryCallback?: () => void) => void
    ): Promise<void> {
        try {
            this.isRosterActionLoading = true;
            await networkOptimization.renameRoster(rosterId, revisionId, revisionName, notes, controller.signal);
            this.updateRevisionInRoster(rosterId, revisionId, revisionName, notes);
            this.showToastNotification('success', 'Roster renamed successfully', `Updated to ${revisionName}`);
        } catch (error) {
            handleApiError(
                error,
                'Failed to Rename Roster',
                () => {
                    const newController = new AbortController();
                    this.renameRoster(rosterId, revisionId, revisionName, notes, newController, handleApiError);
                }
            );
            throw error;
        } finally {
            this.isRosterActionLoading = false;
        }
    }

    async deleteRoster(
        rosterId: number,
        revisionId: number,
        controller: AbortController,
        handleApiError: (error: unknown, title: string, retryCallback?: () => void) => void
    ): Promise<void> {
        try {
            this.isRosterActionLoading = true;
            await networkOptimization.deleteRoster(rosterId, revisionId, controller.signal);
            this.deleteRevisionFromRoster(rosterId, revisionId);
            this.showToastNotification('success', 'Roster deleted successfully', 'The roster revision has been removed');
        } catch (error) {
            handleApiError(
                error,
                'Failed to Delete Roster',
                () => {
                    const newController = new AbortController();
                    this.deleteRoster(rosterId, revisionId, newController, handleApiError);
                }
            );
            throw error;
        } finally {
            this.isRosterActionLoading = false;
        }
    }

    /**
     * Reset roster store state to initial values
     */
    reset(): void {
        runInAction(() => {
            this.loadingRevisionRecalculation = false;
            this.isRosterActionLoading = false;
            this.loadingProviderRosters = false;
            this.providerRostersLoaded = false;
            this.providerRostersLoadFailed = false;
            this.providerRostersRequest = null;
            this._providerRosters = [];
        });
    }
}
